import { Component, ContentChild, Input, output, TemplateRef, ViewChild} from '@angular/core';
import { DraftService } from 'src/app/modules/ngx-draft';
import { DraftCreateInfo } from 'src/app/modules/ngx-draft/lib/draft.service';
import { VSearchComponent } from '../v-search/v-search.component';
export type DraftOptions = {baseURI: string, anchor: string, options?: DraftCreateInfo};

/**
 * Component which is used often for displaying data entered in steppers.
 * It comes with a header, a searchbar and expandable panels for each entry (with the help from v-overview-panel).
 * @name Overview
 * @tags Summary, Data, Page, Display
 * @example
 * ```
 *  %v-overview{title: "Overview example title", [data]: "overviewExample"}
 *      %ng-template{#title, let-entry}
 *          .font-bold {{entry.title}}
 *      %ng-template{#action}
 *          %v-row
*              %v-button{icon: "copy", config: "TextIcon"}
*              %v-button{icon: "edit", config: "TextIcon"}
 *      %ng-template{#description, let-entry}
 *          %span {{entry.description}}
 * ```
 */
@Component({
    selector:    'v-overview',
    templateUrl: './v-overview.component.haml',
    styleUrls:   ['./v-overview.component.sass'],
    standalone:  false
})
export class VOverviewComponent<T extends object = {}> {
    private _data: T[] = [];

    /**
     * It's the title shown on the very top before everything in h1. The default value translates to "Overview".
     * @default "Übersicht"
     */
    @Input() title = "Übersicht";
    /**
     * Add a filter function/closure to include a searchbar.
     * Function must take a search string and a list of elements.
     * 
     * Outside influences can affect the filter function. 
     * To trigger the filter after having outside changes call updateSearch.
     */
    @Input() public search: (term: string, arr: T[]) => T[];
    @Input({ required: true })
    public set data(value: T[]) {
        this._data = value;
        // data might be changed more than once from the outside (e.g. person absence page)
        if (!this.search)this.filteredData = this.data;
    }
    public get data(): T[] {
        return this._data;
    }
    /**
     * Used in v-overview-panel to add extra styling classes to it.
     */
    @Input() entryClasses = '';
    @Input() titleProperty: string | ((obj: T) => string) = "title";
    @Input() descriptionProperty = "description";
    /**
     * Not used in the haml. Used in the copy & edit methods to fetch the id of the entry in order to create a draft. 
     * @default "ID"
     */
    @Input() idProperty: string | ((obj: T) => string) = "ID";
    /**
     * It's a property which you bind to. It's used when you want to edit or copy an entry.
     * @example [draft]: "draft"
     * ```ts
     *  public draft: DraftOptions = {
     *      baseURI: "terminal",
     *      anchor:  "general",
     *      options: {
     *          cancelURL: ["/", "terminals-overview"]
     *      }
     *  };
     * ```
     */
    @Input() draft: DraftOptions;
    /**
     * It's for whether to include the copy functionality in the panel or not.
     * @default true
     */
    @Input() hasCopy = true;
    /**
     * It's for whether to include the edit functionality in the panel or not.
     * @default true
     */
    @Input() hasEdit = true;
    /**
     * A text shown when "data" is empty. It translates to "There are no entries yet.".
     * @default "Es sind noch keine Einträge vorhanden."
     */
    @Input() emptyListText: string = "Es sind noch keine Einträge vorhanden.";
    /**
     * Used in the v-overview-panel for whether padding should be added or not.
     * @default true
     */
    @Input() padContent = true;
    @Input() focus?: string;

    onExpand = output<{value: T, index: number, state: "expand" | "collapsed"}>();


    public filteredData: T[];

    @ViewChild("searchbar", {static: false}) searchbar: VSearchComponent;

    @ContentChild('header', { static: true})
    headerTemplateRef: TemplateRef<unknown>;

    @ContentChild('headerTitle', { static: true})
    headerTitleTemplateRef: TemplateRef<unknown>;
    /**
     * Add a filter template to include a popup-button.
     */
    @ContentChild('filter', { static: false})
    filterTemplateRef: TemplateRef<unknown>;
    @ContentChild('title', { static: false})
    titleTemplateRef: TemplateRef<unknown>;

    @ContentChild('center', { static: false})
    centerTemplateRef: TemplateRef<unknown>;

    @ContentChild('action', { static: false})
    actionTemplateRef: TemplateRef<unknown>;

    @ContentChild('description', { static: false})
    descriptionTemplateRef: TemplateRef<unknown>;

    constructor(private draftService: DraftService){}

    public updateSearch(): void {
        this.searchbar?.update();
    }

    public hasDescription(entry: T){
        const hasTemplate = !!this.descriptionTemplateRef;
        const hasProp = ![null, undefined].includes(this.descriptionProperty);
        const desc = ![null, undefined, ''].includes(((entry as Record<string, unknown>)[this.descriptionProperty] as string)?.trim());
        return hasTemplate || (hasProp && desc);
    }

    async copy(entry: T, event?: Event){
        event?.stopPropagation();
        const ID = typeof this.idProperty === "string" ? (entry as Record<string, unknown>)[this.idProperty] as string : this.idProperty(entry);
        this.draftService.createDraft(this.draft.baseURI, this.draft.anchor, {
            ...this.draft.options,
            copy:     true,
            RecordID: ID,
        }).catch(console.error);
    }
    edit(entry: T, event?: Event){
        event?.stopPropagation();
        const ID = typeof this.idProperty === "string" ? (entry as Record<string, unknown>)[this.idProperty] as string : this.idProperty(entry);
        this.draftService.createDraft(this.draft.baseURI, this.draft.anchor, {
            ...this.draft.options,
            RecordID: ID,
        }).catch(console.error);
    }

    getTitle(entry: T){
        return typeof this.titleProperty === "string" ? (entry as Record<string, unknown>)[this.titleProperty] as string : this.titleProperty(entry);
    }
    getDescription(entry: T){
        return (entry as Record<string, unknown>)[this.descriptionProperty] as string;
    }

    isCollapsed(entry: T) {
        if (!this.focus) return true;
        const ID = typeof this.idProperty === "string" ? (entry as Record<string, unknown>)[this.idProperty] as string : this.idProperty(entry);
        return this.focus !== ID;
    }

    onPanelExpand(event: {value: T, state: "expand" | "collapsed"}, index: number){
        this.onExpand.emit({...event, index});
    }
}

